import PropTypes from "prop-types"
import { createContext, useCallback, useContext, useRef, useState } from "react"

import { useFragmentRouting } from "./useFragmentRouting"

const ChatDrawerContext = createContext()

ChatDrawerProvider.propTypes = {
  children: PropTypes.node.isRequired,
  pcoChatEnabled: PropTypes.bool,
}

export function ChatDrawerProvider({ children, pcoChatEnabled = false }) {
  const [isOpen, setIsOpen] = useState(false)
  const { route, setRoute, lastOpenRoute } = useFragmentRouting()
  const openerRef = useRef(null)

  const openDrawer = () => {
    openerRef.current = findOpener()
    if (lastOpenRoute && !route) setRoute(lastOpenRoute)
    setIsOpen(true)
  }

  const closeDrawer = useCallback(() => {
    setIsOpen(false)
    setRoute(null)
    restoreFocusTo(openerRef.current)
    openerRef.current = null
  }, [setRoute])

  const toggleDrawer = () => {
    isOpen ? closeDrawer() : openDrawer()
  }

  // A slow chat load must not steal focus from wherever the user has since gone.
  const focusDrawerContentIfStillExpected = (element) => {
    if (!element) return
    if (focusIsInDrawerOrUnclaimed(openerRef.current)) element.focus()
  }

  return (
    <ChatDrawerContext.Provider
      value={{
        closeDrawer,
        focusDrawerContentIfStillExpected,
        isOpen,
        openDrawer,
        pcoChatEnabled,
        route,
        setRoute,
        toggleDrawer,
      }}
    >
      {children}
    </ChatDrawerContext.Provider>
  )
}

export const useChatDrawer = () => useContext(ChatDrawerContext)

// Still on body for route-driven opens, and for every click in Safari.
// Both navs toggle with `display: none`, which is what `offsetParent` reflects.
function findOpener() {
  const active = document.activeElement
  if (active && active !== document.body) return active

  return (
    Array.from(document.querySelectorAll(".ChatButton")).find(
      (button) => button.offsetParent !== null,
    ) ?? null
  )
}

function restoreFocusTo(opener) {
  if (!opener || !opener.isConnected) return
  if (focusIsInDrawerOrUnclaimed(opener)) opener.focus()
}

function focusIsInDrawerOrUnclaimed(opener) {
  const active = document.activeElement
  if (!active || active === document.body) return true
  if (active === opener) return true
  return Boolean(document.getElementById("chat-drawer")?.contains(active))
}
